import { Injectable } from '@angular/core';
import { Observable, of, from, throwError } from 'rxjs';
import { map, publishReplay, refCount, switchMap } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';

import { CommonApiService } from '../../shared/services/common-api.service';
import { environment } from '../../../environments/environment';

const PlainTextToHtml = (text): string => {
  text = text
    // Encode <>.
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    // Creates paragraphs for double line breaks and change single line breaks to <br>s.
    .replace(/\n\n/g, '</p><p>')
    .replace(/\n/g, '<br>')
    // Preserve trailing spaces (only the first and last one – the rest is handled below).
    .replace(/^\s/, '&nbsp;')
    .replace(/\s$/, '&nbsp;')
    // Preserve other subsequent spaces now.
    .replace(/\s\s/g, ' &nbsp;');

  if (text.indexOf('</p><p>') > -1) {
    // If we created paragraphs above, add the trailing ones.
    text = '<p>' + text + '</p>';
  }

  // TODO:
  // * What about '\nfoo' vs ' foo'?

  return text;
};

export class CKEditorImageUploadAdapter {
  private canceled: boolean;

  constructor(private loader: any, private apiService: CommonApiService) {}

  upload(): Promise<any> {
    this.canceled = false;

    return this.loader.file.then((file) => {
      return this.postFile(file)
        .pipe(
          map((uploadedImageUrl) => {
            if (this.canceled) {
              throw new Error('aborted');
            }
            return { default: uploadedImageUrl };
          })
        )
        .toPromise();
    });
  }

  abort(): void {
    this.canceled = true;
  }

  private postFile(file: any): Observable<string> {
    return this.apiService
      .uploadImage(file)
      .pipe(map((response) => response[0].url));
  }
}

@Injectable({
  providedIn: 'root',
})
export class EditorLoaderService {
  private editorLoad$: Observable<any>;

  constructor(
    private commonApiService: CommonApiService,
    private translateService: TranslateService
  ) {}

  loadEditor(): Observable<any> {
    if (!this.editorLoad$) {
      let editorLoad$;

      if (window['BukEditor']) {
        editorLoad$ = of(window['BukEditor']);
      } else {
        editorLoad$ = new Observable((observer) => {
          const script = document.createElement('script');

          script.onload = (): void => {
            observer.next(window['BukEditor']);
            observer.complete();
          };
          script.onerror = (error): void => {
            observer.error(error);
          };

          script.src = `/assets/ckeditor5/ckeditor.js?${environment.editorVersion}`;
          script.async = true;
          document.body.appendChild(script);
        });
      }

      this.editorLoad$ = editorLoad$.pipe(publishReplay(1), refCount());
    }

    return this.editorLoad$;
  }

  createEditor(sourceElement: HTMLElement, config?: any): Observable<any> {
    return this.loadEditor().pipe(
      switchMap((editorImpl) => {
        if (!editorImpl) {
          return throwError('CKEditor does not support this browser');
        }

        return from(editorImpl.create(sourceElement, config));
      }),
      map((editorInstance: any) => {
        editorInstance.plugins.get('FileRepository').createUploadAdapter = (
          loader
        ): CKEditorImageUploadAdapter => {
          return new CKEditorImageUploadAdapter(loader, this.commonApiService);
        };

        const editingView = editorInstance.editing.view;

        // backspace 누르면 자모 분리된 글자가 나타나는 버그 수정
        editingView.document.on(
          'keydown',
          (evt, data) => {
            if (data.domEvent.key === 'Backspace') {
              data.keyCode = 8;
            }
          },
          { priority: 'highest' }
        );

        // 북이오 링크 붙여넣으면 oembed 동작하도록 설정
        editingView.document.on('clipboardInput', (evt, data) => {
          if (editorInstance.isReadOnly) {
            return;
          }

          const dataTransfer = data.dataTransfer;

          const text = dataTransfer.getData('text/plain');

          if (
            !/^https:\/\/(?:(?:s|d|r|alpha|dev)\.)?buk\.io\/@([^\s]+)/.test(
              text.trim()
            )
          ) {
            return;
          }

          const content = PlainTextToHtml(text);

          data.content = editorInstance.data.processor.toView(content);
        });

        // FIXME: 사파리에서 에디터 내용을 메모장에 붙여넣으면 인코딩이 깨지는 문제 임시방편 => 일반 텍스트로 복사
        if (
          /Safari/.test(navigator.userAgent) &&
          !/Chrome/.test(navigator.userAgent)
        ) {
          editingView.document.on(
            'clipboardOutput',
            (e, data) => {
              data.dataTransfer.setData('text/html', '');
            },
            { priority: 'lowest' }
          );
        }

        return editorInstance;
      })
    );
  }
}
